const PDF_DATA_PREFIX = 'data:application/pdf;base64,';
const HTTP_URL_REGEX = /^https?:\/\//i;
const PDF_DATA_URL_REGEX = /^data:application\/pdf;base64,/i;
const PROTOCOL_RELATIVE_URL_REGEX = /^\/\//;
const RELATIVE_URL_REGEX = /^(\/|\.\/|\.\.\/|assets\/|uploads\/|files\/)/i;

const toCleanString = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const toBase64Only = (value) => {
  return value.replace(PDF_DATA_URL_REGEX, '').replace(/\s/g, '');
};

const base64ToPdfBlob = (base64) => {
  const binaryString = atob(base64);
  const byteArrays = [];
  const chunkSize = 1024;

  for (let offset = 0; offset < binaryString.length; offset += chunkSize) {
    const slice = binaryString.slice(offset, offset + chunkSize);
    const byteNumbers = new Array(slice.length);

    for (let index = 0; index < slice.length; index += 1) {
      byteNumbers[index] = slice.charCodeAt(index);
    }

    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: 'application/pdf' });
};

const buildAbsoluteUrl = (source) => {
  if (!source) return '';
  if (HTTP_URL_REGEX.test(source)) return source;
  if (PROTOCOL_RELATIVE_URL_REGEX.test(source)) {
    return `${window.location.protocol}${source}`;
  }
  if (!RELATIVE_URL_REGEX.test(source)) return source;

  const apiBaseUrl = toCleanString(process.env.REACT_APP_API_BASE_URL);
  const base = apiBaseUrl || window.location.origin;

  try {
    return new URL(source, base).href;
  } catch (urlError) {
    return source;
  }
};

const isLikelyBase64 = (value) => {
  if (!value || value.length < 50) return false;
  const sanitized = value.replace(/\s/g, '');
  return /^[A-Za-z0-9+/=]+$/.test(sanitized);
};

const unwrapDocumentSource = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';

    if (trimmedValue.startsWith('[') || trimmedValue.startsWith('{')) {
      try {
        return unwrapDocumentSource(JSON.parse(trimmedValue));
      } catch {
        return trimmedValue;
      }
    }

    return trimmedValue;
  }

  if (Array.isArray(value)) {
    return unwrapDocumentSource(value[0]);
  }

  if (typeof value === 'object') {
    return (
      unwrapDocumentSource(value.url) ||
      unwrapDocumentSource(value.path) ||
      unwrapDocumentSource(value.file) ||
      unwrapDocumentSource(value.href) ||
      ''
    );
  }

  return '';
};

export const sanitizePdfFileName = (fileName) => {
  const sanitizedName = toCleanString(fileName)
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ');

  const finalName = sanitizedName || 'invoice.pdf';
  return finalName.toLowerCase().endsWith('.pdf')
    ? finalName
    : `${finalName}.pdf`;
};

const openDocumentInBrowser = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = sanitizePdfFileName(fileName);
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const resolvePdfSource = (source) => {
  const normalizedSource = toCleanString(unwrapDocumentSource(source));
  if (!normalizedSource) return '';

  if (HTTP_URL_REGEX.test(normalizedSource)) {
    return normalizedSource;
  }

  if (PROTOCOL_RELATIVE_URL_REGEX.test(normalizedSource)) {
    return buildAbsoluteUrl(normalizedSource);
  }

  if (RELATIVE_URL_REGEX.test(normalizedSource)) {
    return buildAbsoluteUrl(normalizedSource);
  }

  if (PDF_DATA_URL_REGEX.test(normalizedSource)) {
    return `${PDF_DATA_PREFIX}${toBase64Only(normalizedSource)}`;
  }

  if (isLikelyBase64(normalizedSource)) {
    return `${PDF_DATA_PREFIX}${toBase64Only(normalizedSource)}`;
  }

  return buildAbsoluteUrl(normalizedSource);
};

export const downloadPdfFile = async (
  source,
  fileName = 'invoice.pdf',
  options = {},
) => {
  const { authToken = '' } = options;
  const normalizedSource = toCleanString(unwrapDocumentSource(source));
  if (!normalizedSource) {
    throw new Error('Invoice file is not available.');
  }

  const resolvedSource = resolvePdfSource(normalizedSource);
  if (!resolvedSource) {
    throw new Error('Invoice file is not available.');
  }

  let blob;
  if (
    HTTP_URL_REGEX.test(resolvedSource) ||
    PROTOCOL_RELATIVE_URL_REGEX.test(resolvedSource) ||
    RELATIVE_URL_REGEX.test(resolvedSource)
  ) {
    try {
      const response = await fetch(resolvedSource, {
        method: 'GET',
        headers: authToken
          ? {
              Authorization: `Bearer ${authToken}`,
            }
          : {},
      });
      if (!response.ok) {
        throw new Error('Unable to fetch invoice file.');
      }
      blob = await response.blob();
    } catch (downloadError) {
      openDocumentInBrowser(resolvedSource, fileName);
      return;
    }
  } else {
    blob = base64ToPdfBlob(toBase64Only(resolvedSource));
  }

  const fileUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = sanitizePdfFileName(fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(fileUrl);
};
